import { markdownToInlineReact } from '@/lib/markdown';
import { Card, CardBody } from '@/components/wrapped';
import styles from '@/styles/InfoCard.module.scss';

type InfoCardGroupProps = {
  columns?: 2 | 3 | 4;
  style?: 'small';
  variant?: 'primary';
  children?: React.ReactNode;
};

export function InfoCardGroup({ columns = 2, style, variant, children }: InfoCardGroupProps) {
  const colClass = columns === 4 ? styles.cols4 : columns === 3 ? styles.cols3 : '';
  const styleClass = style === 'small' ? styles.small : '';
  const variantClass = variant === 'primary' ? styles.groupPrimary : '';
  return (
    <div className={`${styles.infoCardGroup} ${colClass} ${styleClass} ${variantClass}`}>
      {children}
    </div>
  );
}

type InfoCardProps = {
  title?: React.ReactNode;
  style?: string;
  variant?: 'primary';
  color?: 'blue' | 'green' | 'cyan';
  badge?: string;
  icon?: React.ReactNode;
  width?: string;
  children?: React.ReactNode;
};

function InfoCardBadge({ badge, color }: { badge: string; color?: 'blue' | 'green' | 'cyan' }) {
  const colorClass = color === 'green' ? styles.badgeGreen : color === 'cyan' ? styles.badgeCyan : styles.badgeBlue;
  return <span className={`${styles.badge} ${colorClass}`}>{badge}</span>;
}

function InfoCardTitle({ title, icon }: { title: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className={styles.title}>
      {icon && <span className={styles.iconTile}>{icon}</span>}
      {typeof title === 'string' ? markdownToInlineReact(title) : title}
    </div>
  );
}

export function InfoCard({ title, style, variant, color, badge, icon, width, children }: InfoCardProps) {
  const variantClass = variant === 'primary' ? styles.primary : '';
  const colorClass = color === 'green' ? styles.accentGreen : color === 'cyan' ? styles.accentCyan : '';
  const inlineStyle = width ? { width } : undefined;
  if (style === 'border' || style === 'accent') {
    const styleClass = style === 'accent' ? styles.accent : styles.border;
    return (
      <Card className={`${styles.infoCard} ${variantClass} ${styleClass} ${colorClass}`} style={inlineStyle}>
        <CardBody>
          {badge && <InfoCardBadge badge={badge} color={color} />}
          {title && <InfoCardTitle title={title} icon={icon} />}
          {children}
        </CardBody>
      </Card>
    )
  }
  return (
    <div className={`${styles.infoCard} ${variantClass}`} style={inlineStyle}>
      {title && <InfoCardTitle title={title} icon={icon} />}
      {children}
    </div>
  );
}
